<div class="relative-details-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-state">
    <div class="loading-spinner"></div>
    <p>Loading relative details...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="errorMessage && !isLoading" class="error-state">
    <div class="error-icon">⚠️</div>
    <h2>Error</h2>
    <p>{{ errorMessage }}</p>
    <button class="btn btn-primary" (click)="goBack()">Go Back</button>
  </div>

  <!-- Relative Details -->
  <div *ngIf="relative && !isLoading" class="relative-details">
    <!-- Header -->
    <div class="details-header">
      <button class="back-btn" (click)="goBack()" title="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Back</span>
      </button>
      <h1>Relative Details</h1>
    </div>

    <!-- Profile Section -->
    <div class="profile-section">
      <div class="profile-avatar">
        <img *ngIf="relative.profileImageUrl" class="avatar-img" [src]="relative.profileImageUrl" alt="Profile avatar" />
        <div class="avatar-circle" *ngIf="!relative.profileImageUrl">
          {{ getInitials(relative) }}
        </div>
      </div>
      <div class="profile-info">
        <h2>{{ relative.firstName }} {{ relative.lastName }}</h2>
        <p class="age-info" *ngIf="!relative.isDeceased">{{ calculateAge(relative.dateOfBirth) }} years old</p>
        <p class="deceased-info" *ngIf="relative.isDeceased">Deceased</p>
        <p class="relationship-type" *ngIf="relative.relationshipType">{{ relative.relationshipType }}</p>
      </div>
    </div>

    <!-- Details Grid -->
    <div class="details-grid">
      <!-- Personal Information -->
      <div class="details-card">
        <h3>Personal Information</h3>
        <div class="detail-item">
          <label>Full Name:</label>
          <span>{{ relative.firstName }} {{ relative.middleName || '' }} {{ relative.lastName }}</span>
        </div>
        <div class="detail-item">
          <label>Date of Birth:</label>
          <span>{{ formatDate(relative.dateOfBirth) }}</span>
        </div>
        <div class="detail-item">
          <label>Gender:</label>
          <span>{{ relative.gender }}</span>
        </div>
        <div class="detail-item" *ngIf="relative.email">
          <label>Email:</label>
          <span>{{ relative.email }}</span>
        </div>
        <div class="detail-item">
          <label>Mobile:</label>
          <span>{{ relative.mobileNumber }}</span>
        </div>
      </div>

      <!-- Professional Information -->
      <div class="details-card" *ngIf="relative.qualification || relative.profession">
        <h3>Professional Information</h3>
        <div class="detail-item" *ngIf="relative.qualification">
          <label>Qualification:</label>
          <span>{{ relative.qualification }}</span>
        </div>
        <div class="detail-item" *ngIf="relative.profession">
          <label>Profession:</label>
          <span>{{ relative.profession }}</span>
        </div>
      </div>

      <!-- Location Information -->
      <div class="details-card" *ngIf="relative.location">
        <h3>Location</h3>
        <div class="detail-item">
          <label>Location:</label>
          <span>{{ relative.location }}</span>
        </div>
      </div>

      <!-- Family Information -->
      <div class="details-card">
        <h3>Family Information</h3>
        <div class="detail-item">
          <label>Referral Code:</label>
          <span class="referral-code">{{ relative.referralCode }}</span>
        </div>
        <div class="detail-item" *ngIf="relative.parentReferralCode">
          <label>Parent Referral Code:</label>
          <span class="referral-code">{{ relative.parentReferralCode }}</span>
        </div>
        <div class="detail-item" *ngIf="relative.spouseReferralCode">
          <label>Spouse Referral Code:</label>
          <span class="referral-code">{{ relative.spouseReferralCode }}</span>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions-section">
      <button class="btn btn-primary" (click)="goToHierarchy()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 22V12H15V22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        View Family Tree
      </button>
      <button class="btn btn-primary" (click)="goToTimeline()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
        </svg>
        View Timeline
      </button>
      <button class="btn btn-primary" (click)="goToFamilyTimeline()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Family Timeline
      </button>
      <button class="btn btn-secondary" (click)="goBack()" title="Go back">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Back</span>
      </button>
    </div>
  </div>
</div>